import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { IAppState, IInvitation, IPolicy } from 'types';
import { connect } from 'react-redux';
import styles from './styles/share.scss';
import {
  GetInvitationsForFamilyId,
  DeleteInvitation,
  ClearInvitationErrors,
  CreateInvitation,
  GetInvitationsByUserId,
} from 'actions/invitation-actions';
import { DeleteUserFromFamily, SetCurrentFamily } from 'actions/user';
import InvitationList from 'components/invitation-list';
import { convertToNZDate, getFamily, PAGE_VIEW_EVENTS } from 'helpers';
import Title from 'components/title';
import CreateInvitationForm from 'components/create-invitation';
import { SelectPolicy } from 'actions';
import SharedWithMe from './SharedWithMe';
import { BackToTop } from 'components/quashed';
import useTracking from 'hooks/useTracking';

interface IDispatchProps {
  getInvitationsForFamilyId(familyId: string): void;
  getInvitationsByUserId(userId: string): void;
  deleteInvitation(invitationId: string): void;
  deleteUserFromFamily(userId: string, familyId: string): void;
  createInvitation(invitation: IInvitation): void;
  clearInvitationErrors(): void;
  setCurrentFamily(id: string): void;
  selectPolicy(policy: IPolicy): void;
}

const normalizeInvitations = (invitations, isShared = false) => {
  return invitations.map((inv) => ({
    id: inv.id,
    sk: inv.sk,
    name: isShared ? `${inv.ownerName} ${inv.ownerLastName}` : inv.name,
    email: isShared ? inv.ownerEmail : inv.invitedEmail,
    relationship: inv.relationship,
    createdAt: convertToNZDate(inv.createdAt),
    processed: inv.processed,
  }));
};

export const InvitationHistory = (props: IAppState & IDispatchProps) => {
  const {
    user,
    invitation,
    policies,
    getInvitationsForFamilyId,
    deleteInvitation,
    deleteUserFromFamily,
    createInvitation,
    clearInvitationErrors,
    getInvitationsByUserId,
    setCurrentFamily,
    selectPolicy,
  } = props;
  useTracking(PAGE_VIEW_EVENTS.Share);
  const scrollRef = useRef(null);
  const { invitations, error, loading, sharedInvitations } = invitation;
  const { id, families, currentFamilyId } = user;

  const family = getFamily(families);

  useEffect(() => {
    if (family?.id && invitations.length === 0) {
      getInvitationsForFamilyId(family.id);
    }
  }, [families]);

  useEffect(() => {
    if (id && sharedInvitations.length === 0) {
      getInvitationsByUserId(id);
    }
  }, [id]);

  useEffect(() => {
    return () => {
      // Reset familyId to own family when navigating away from shared dashboard
      selectPolicy(null);
      setOwnedFamily();
      clearInvitationErrors();
    };
  }, []);

  const handleSubmit = useCallback(
    (payload: IInvitation) => {
      createInvitation(payload);
    },
    [createInvitation],
  );

  const isShared = useMemo(() => {
    const currentFamily =
      families && families.find(({ id: famId }) => famId === currentFamilyId);
    return currentFamily && !currentFamily.familyOwner;
  }, [families, currentFamilyId]);

  const handleViewClick = ({ sk: familyId }) => setCurrentFamily(familyId);

  const setOwnedFamily = () => setCurrentFamily(family.id);

  const handleScrollToTop = () => {
    scrollRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  if (isShared) {
    return (
      <SharedWithMe
        invitations={invitation.sharedInvitations}
        currentFamilyId={user.currentFamilyId}
        selectedPolicy={policies.selectedPolicy}
        getInvitationsByUserId={getInvitationsByUserId}
        selectPolicy={selectPolicy}
        setOwnedFamily={setOwnedFamily}
      />
    );
  }

  return (
    <>
      <div ref={scrollRef} className={styles.container}>
        <div className={classnames(styles.header)}>
          <Title
            title="Share my insurance dashboard"
            subtitle={
              'Clicking "Share" will inform your chosen person by email that they have access to view your insurance policies.'
            }
            className={styles.titleContainer}
            subtitleClassName={styles.subtitle}
            tooltip={
              <>
                This allows you to <b>share</b> your insurance information with{' '}
                <b>people you trust</b>. Helpful in case of emergencies.
              </>
            }
          />
        </div>
        <div className={styles.content}>
          <CreateInvitationForm
            onSubmit={handleSubmit}
            familyId={family && family.id}
            error={error}
            user={user}
            loading={loading}
          />
          <div className={styles.right}>
            <div className={styles.invitations}>
              <h3 className={styles.title}>Currently shared with</h3>
              <InvitationList
                invitations={normalizeInvitations(invitations)}
                deleteInvitation={deleteInvitation}
                deleteUserFromFamily={deleteUserFromFamily}
                loading={loading}
                emptyText="Share your policies with your loved ones"
                showStatus={true}
              />
            </div>
            <div className={styles.invitations}>
              <h3 className={styles.title}>Shared with me</h3>
              <InvitationList
                className={styles.sharedList}
                invitations={normalizeInvitations(sharedInvitations, true)}
                loading={loading}
                emptyText="No one has shared with you"
                onItemClick={handleViewClick}
              />
            </div>
          </div>
        </div>
      </div>
      <BackToTop onClick={handleScrollToTop} />
    </>
  );
};

const mapStateToProps = (state: IAppState) => ({
  ...state,
});

const mapDispatchToProps = {
  getInvitationsForFamilyId: GetInvitationsForFamilyId,
  getInvitationsByUserId: GetInvitationsByUserId,
  deleteInvitation: DeleteInvitation,
  deleteUserFromFamily: DeleteUserFromFamily,
  createInvitation: CreateInvitation,
  clearInvitationErrors: ClearInvitationErrors,
  setCurrentFamily: SetCurrentFamily,
  selectPolicy: SelectPolicy,
};

export default connect<IAppState, IDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(InvitationHistory);
